import React from 'react';
import PropTypes from 'prop-types';
import { flatMap, keys, values, max, merge, startCase } from 'lodash';
import { FlexLoadingSpinner } from 'components/helpers/LoadingSpinner';
import 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const COLORS = {
  liked: { backgroundColor: ['#5ac9ac', '#ffce15', '#e7831e', '#ea054d'] },
  levels: { backgroundColor: 'rgba(49,204,255,1)' },
};

const LIKED_SYMBOLS = {
  cry: '\ue900',
  liked: '\ue901',
  normal: '\ue902',
  notLiked: '\ue903',
}

const BARCHART_DEFAULT = {
  label: '',
};

const chartData = (data, chartType) => {
  const datasets = [merge({}, BARCHART_DEFAULT, COLORS[chartType], { data: values(data.data) })];
  const ticksTransform = {
    liked: d => LIKED_SYMBOLS[d],
    levels: d => startCase(d),
  }
  return {
    labels: keys(data.data).map(ticksTransform[chartType]),
    datasets,
  };
}

const CHART_OPTIONS_BASE = (maxValue) => {
  return {
    scales: {
      x: { gridLines: { display: false } },
      y: {
        gridLines: { display: false, drawBorder: false },
        ticks: { display: false, min: 0, max: maxValue },
      },
    },
    layout: {
      padding: {
        top: 5,
      },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end',
        display: true,
        font: { weight: 'bold' },
        formatter: (value, _context) => `${Math.round(value)}%`,
      },
    },
  }
};

const CHART_OPTIONS = {
  liked: {
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label} ${Math.round(value)}%`;
          },
        },
        titleFont: {
          family: 'sparkler',
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            color: COLORS.liked.backgroundColor,
            family: 'sparkler',
            size: 16,
          },
        },
      },
    },
  },
  levels: {
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label} ${Math.round(value)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 16,
          },
        },
      },
    },
  },
}

const BarChart = ({ data, chartTypes, cls, isLoading }) => {
  const maxValue = max(flatMap(data, d => values(d.data))) + 20;
  const charts = data.map((d, idx) =>
    <div key={`bar-chart-${idx}`} className='column medium-6 small-12 o-dashboard-card__wrap'>
      <strong>{d.label}</strong>
      <div className='o-dashboard-card u-padding-bottom--small cs-bg--light'>
        <div className='column'>
          <div className='o-chart--bar'>
            {isLoading ? (<FlexLoadingSpinner/>) :(
              <Bar data={chartData(d, chartTypes[idx])} options={merge({}, CHART_OPTIONS_BASE(maxValue), CHART_OPTIONS[chartTypes[idx]])} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div className={cls}>
      <div className='row'>
        {charts}
      </div>
    </div>
  )
}


BarChart.propTypes = {
  chartTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  cls: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
}

export default BarChart;
